import { useContext, useEffect, useState } from 'react'
import { Navigate, Outlet, useMatch, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { MatterDialogType, MattersActions } from '@/matter/store/slice'
import { RootState } from '@/store/store'
import { AuthContext } from '@/context/auth-context'
import AnonFeatureGuard from '@/components/guards/AnonFeatureGuard'
import { FileUploadProvider } from '@/uppy-file-upload/context/FileUploadContext'
import { isPendingMatterInvite } from '@/matter/utils/is-pending-matter-invite'
import { useIsAccountCollaborationCapable } from '@/collaboration/hooks/useIsAccountCollaborationCapable'
import { usePollWhileVisible } from '@/hooks/usePollWhileVisible'
import { kMatterPollingIntervalMs } from '@/matter/constants'
import { getMatterDetail } from '@/matter/fetch/get-matter-detail'
import { MatterType, MatterViewerRole } from '@/matter/store/schemas'

/**
 * MatterLayout - Parent route component for matter-first URL hierarchy
 *
 * This layout sits at `/dashboard/m/:matterId` and provides matter context to all
 * child routes (chronology, billing-summaries, chat, files).
 *
 * Responsibilities:
 * - URL → Redux sync: Syncs the matterId from URL to Redux state (one-way)
 * - Validation: Redirects to matters list if the matter ID is invalid
 *
 * The URL is the source of truth for matter context:
 * - `/m/:matterId` in URL → that matter is active
 * - No `/m/:matterId` (e.g., `/dashboard/matters`) → no matter is active
 *
 * Child components should use `useParams()` to access the matterId directly.
 * Redux state is synced for sidebar and other non-route components.
 *
 * @see Phase 4 of routing refactor plan
 */
export default function MatterLayout() {
  const { matterId } = useParams<{ matterId: string }>()
  const dispatch = useAppDispatch()
  const authContext = useContext(AuthContext)
  const user = authContext?.user
  const userIsSignedIn = !!user && !user.isAnonymous
  const isAccountCollaborationCapable = useIsAccountCollaborationCapable()

  // Redux state
  const mattersById = useAppSelector((state: RootState) => state.mattersState.matters)
  const initialLoadComplete = useAppSelector((state: RootState) => state.mattersState.initialLoadComplete)
  const isReferenceViewer = !!useMatch('/dashboard/m/:matterId/reference-viewer/*')
  const isAgentChat = !!useMatch('/dashboard/m/:matterId/chat/agent/*')
  const isChatHistory = !!useMatch('/dashboard/m/:matterId/chats')
  const [sourceAccess, setSourceAccess] = useState<{ matterId: string; userId: string; allowed: boolean } | null>(null)

  // Check if the matterId from the URL is valid
  const isValidMatterId = matterId ? matterId in mattersById : false

  const matter = mattersById[matterId ?? '']
  const isPendingInvite = isPendingMatterInvite(matter)
  const needsSourceAccess = initialLoadComplete && userIsSignedIn && (isReferenceViewer || isAgentChat || isChatHistory) && !!matterId && !isValidMatterId
  const sourceAccessResolved = sourceAccess?.matterId === matterId && sourceAccess?.userId === user?.uid
  const canViewPrivateSource = needsSourceAccess && sourceAccessResolved && sourceAccess?.allowed

  // Authorize private chat/source links without adding this workspace to shared matter lists.
  useEffect(() => {
    if (!needsSourceAccess || !matterId || !user?.uid) return
    let cancelled = false
    const userId = user.uid
    void getMatterDetail(matterId).then(
      ({ matter: sourceMatter }) => {
        if (cancelled) return
        const allowed =
          sourceMatter.id === matterId &&
          sourceMatter.type === MatterType.OUTLOOK_WORKSPACE &&
          sourceMatter.viewer_role === MatterViewerRole.Owner &&
          sourceMatter.user_id === userId
        dispatch(MattersActions.setPersonalWorkspace(allowed ? sourceMatter : null))
        setSourceAccess({
          matterId,
          userId,
          allowed,
        })
      },
      () => {
        if (!cancelled) setSourceAccess({ matterId, userId, allowed: false })
      }
    )
    return () => {
      cancelled = true
    }
  }, [needsSourceAccess, matterId, user?.uid, dispatch])

  // Whether this matter is accessible to the current user
  const isMatterAccessible = isValidMatterId && !isPendingInvite

  useEffect(() => {
    if (canViewPrivateSource) dispatch(MattersActions.setActiveMatterId({ matterId: isAgentChat || isChatHistory ? matterId ?? null : null }))
  }, [canViewPrivateSource, dispatch, isAgentChat, isChatHistory, matterId])

  // Only collaboration-capable accounts need polling for other members' changes.
  const shouldSyncMatterData = isMatterAccessible && isAccountCollaborationCapable

  // Simple one-way sync: URL → Redux (for sidebar and non-route components)
  // Note: activeMatterId intentionally excluded from deps - this effect only syncs URL→Redux,
  // not Redux→URL. Including it would cause the effect to "correct" user-initiated changes
  // (like clicking Home to clear matter context) before navigation completes.
  useEffect(() => {
    if (!initialLoadComplete) return
    if (!matterId || !isMatterAccessible) return

    dispatch(MattersActions.setActiveMatterId({ matterId }))
  }, [initialLoadComplete, matterId, isMatterAccessible, dispatch])

  // Open the accept invite dialog when a user navigates directly to a pending-invite matter URL.
  // The dialog is mounted at DashboardLayout so it renders after the redirect below.
  useEffect(() => {
    if (!initialLoadComplete || !isPendingInvite || !matterId) return
    dispatch(MattersActions.openDialog({ dialogType: MatterDialogType.AcceptInvite, matterId }))
  }, [initialLoadComplete, isPendingInvite, matterId, dispatch])

  /**
   * Loads matter details on entry for every account, including personal accounts whose banner
   * dismissals arrive in this response. Collaboration-only polling remains separate.
   */
  useEffect(() => {
    if (!initialLoadComplete || !isMatterAccessible || !matterId) return
    dispatch(MattersActions.refreshMatterDetail({ matterId, forceRefresh: false }))
  }, [initialLoadComplete, isMatterAccessible, matterId, dispatch])

  /**
   * Polls this matter so a collaborator's changes to it, and to its membership, show up without a
   * hard refresh.
   */
  usePollWhileVisible(
    () => {
      if (matterId) dispatch(MattersActions.refreshMatterDetail({ matterId, forceRefresh: true }))
    },
    kMatterPollingIntervalMs,
    shouldSyncMatterData
  )

  // Wait for matters to load before validating
  if (userIsSignedIn && !initialLoadComplete) {
    return null
  }
  if (needsSourceAccess && !sourceAccessResolved) return null

  // Redirect to matters list if the matter is inaccessible (invalid ID or unaccepted invite).
  // When the invite is pending, the useEffect above opens AcceptMatterInviteDialog on the matters list.
  if (initialLoadComplete && !isMatterAccessible && !canViewPrivateSource) {
    return <Navigate to="/dashboard/matters" replace />
  }

  return (
    <AnonFeatureGuard showAuthOnGuard={true}>
      <FileUploadProvider>
        <Outlet />
      </FileUploadProvider>
    </AnonFeatureGuard>
  )
}
